import type { ReactNode } from "react";
import { Cloud, CloudOff, Home, Leaf, Puzzle, RefreshCw } from "lucide-react";
import { Link, usePath } from "../app/router";
import { useSession } from "../app/session";
import { useStore } from "../app/store";
import { dayKey } from "../app/clock";
import { dayTotals } from "../app/stats";
import { useSyncStatus } from "../app/sync";
import { Ring } from "./kit";

const NAV = [
  { to: "/today", label: "Today", icon: Home },
  { to: "/puzzles", label: "Puzzles", icon: Puzzle },
  { to: "/you", label: "You", icon: Leaf }
];

export function SyncDot() {
  const { user } = useSession();
  const { state } = useSyncStatus();
  if (!user) return null;
  const label =
    state === "syncing" ? "Syncing…" : state === "offline" ? "Offline: your progress is safe on this device and will sync later" : state === "error" ? "Couldn't sync just now. We'll try again." : "Synced with your account";
  const Icon = state === "syncing" ? RefreshCw : state === "offline" || state === "error" ? CloudOff : Cloud;
  return (
    <span className={"sync-dot " + state} title={label} role="img" aria-label={label}>
      <Icon size={17} />
    </span>
  );
}

function MiniRing() {
  const { secs, goal } = useStore((d) => ({ secs: dayTotals(d, dayKey()).total, goal: d.settings.goalMinutes * 60 }));
  const done = secs >= goal;
  return (
    <Link to="/you" className="mini-ring" aria-label={`${Math.floor(secs / 60)} of ${goal / 60} minutes of thinking today`}>
      <Ring value={secs} max={goal} size={38} stroke={5} done={done}>
        <span className="mini-ring-n">{Math.floor(secs / 60)}</span>
      </Ring>
    </Link>
  );
}

export function Shell({ children, immersive = false }: { children: ReactNode; immersive?: boolean }) {
  const path = usePath();
  const { user } = useSession();
  const name = useStore((d) => d.name);
  const initial = (user?.name || name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className={"shell" + (immersive ? " immersive" : "")}>
      <header className="topbar">
        <div className="wrap topbar-in">
          <Link to="/today" className="brand" aria-label="Ponder home">
            <img src="/favicon-64.png" alt="" width="34" height="34" />
            <span className="wordmark">ponder</span>
          </Link>
          <nav className="topnav" aria-label="Main">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className={"topnav-link" + (path === n.to || (n.to === "/puzzles" && path.startsWith("/p/")) ? " on" : "")}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="topbar-right">
            <SyncDot />
            <MiniRing />
            {user ? (
              <Link to="/you#account" className="avatar" aria-label="Your account">
                {initial}
              </Link>
            ) : (
              <Link to="/login" className="btn small">
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>
      <main className="main">{children}</main>
      {!immersive && (
        <nav className="bottomnav" aria-label="Main">
          {NAV.map((n) => {
            const Icon = n.icon;
            const on = path === n.to || (n.to === "/puzzles" && path.startsWith("/p/"));
            return (
              <Link key={n.to} to={n.to} className={"bottomnav-link" + (on ? " on" : "")}>
                <Icon size={22} />
                <span>{n.label}</span>
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}

